import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import tw, { styled } from 'twin.macro';
import PropTypes from 'prop-types';

import {
  Flex,
  Divider,
  ActionMenu,
  useNotification,
  Button
} from '@sopranodesign/pulse-components';

import { downloadMetricCsv } from '../api/index';
import { slugify, saveBlob, captureElementAsPng } from '../../../utils/index';
import {
  exportableMetricKeys as nonExplorableMetrics,
  notCleanExportable
} from './DragableMetric';

const ActionContainer = styled(Flex)`
  ${tw`absolute top-0 right-0 z-10 mt-2`}
  /* Menu is interactive chrome, not part of the metric. */
  @media print {
    display: none;
  }
`;

const formatDate = date => dayjs(date).format('D MMM YYYY HH:mm');

const MetricMenu = ({
  metric,
  params,
  panelRef,
  canExportCsv,
  canViewMore
}) => {
  const { showSuccess, showError } = useNotification();

  const [exporting, setExporting] = useState(false);

  const { start, end, compare } = params;

  const fileName = `${slugify(metric.name)}-${dayjs(start).format(
    'YYYY-MM-DD'
  )}-to-${dayjs(end).format('YYYY-MM-DD')}`;

  const runExport = async task => {
    setExporting(true);

    try {
      await task();
    } catch (error) {
      // HTTP failures are already reported by the app's global axios interceptor.
      if (!error.response) {
        showError(`Unable to export "${metric.name}". Please try again.`);
      }
    } finally {
      setExporting(false);
    }
  };

  const handleCsv = (raw = true) =>
    runExport(async () => {
      const result = await downloadMetricCsv({
        id: metric.id,
        start,
        end,
        compare,
        raw
      });

      if (result.queued) {
        showSuccess(
          `"${metric.name}" is a large export. We'll email it to you when it's ready.`
        );
        return;
      }

      saveBlob(result.blob, `${fileName}.csv`);
      showSuccess(`Exported "${metric.name}" as CSV.`);
    });

  const handleImage = () =>
    runExport(async () => {
      const blob = await captureElementAsPng(panelRef.current, {
        title: metric.name,
        subtitle: `${formatDate(start)} - ${formatDate(end)}`
      });

      saveBlob(blob, `${fileName}.png`);
      showSuccess(`Exported "${metric.name}" as an image.`);
    });

  const hasClean =
    !nonExplorableMetrics.includes(metric.key) &&
    !notCleanExportable.includes(metric.key);

  return (
    <ActionContainer data-export-ignore>
      <ActionMenu
        position={ActionMenu.POSITIONS.LEFT}
        buttonProps={{ 'aria-label': `metric-menu-${metric.name}` }}>
        <Flex col>
          {canViewMore && !nonExplorableMetrics.includes(metric.key) && (
            <>
              <Button
                variant="inline"
                icon="externalLink"
                as={Link}
                to={{
                  pathname: `dashboard/${metric.id}`,
                  state: { start, end }
                }}>
                View more
              </Button>
              <Divider mtNone mbNone />
            </>
          )}
          {canExportCsv && (
            <>
              <Button
                variant="inline"
                disabled={exporting}
                onClick={() => handleCsv(true)}
                icon="download">
                Export CSV (raw)
              </Button>
              {hasClean && (
                <>
                  <Divider mtNone mbNone />
                  <Button
                    variant="inline"
                    disabled={exporting}
                    onClick={() => handleCsv(false)}
                    icon="download">
                    Export CSV (clean)
                  </Button>
                </>
              )}
              <Divider mtNone mbNone />
            </>
          )}
          <Button
            variant="inline"
            disabled={exporting}
            onClick={handleImage}
            icon="download">
            Export as image
          </Button>
        </Flex>
      </ActionMenu>
    </ActionContainer>
  );
};

MetricMenu.propTypes = {
  metric: PropTypes.shape({
    id: PropTypes.number,
    key: PropTypes.string,
    name: PropTypes.string
  }).isRequired,
  params: PropTypes.object.isRequired,
  panelRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    .isRequired,
  canExportCsv: PropTypes.bool,
  canViewMore: PropTypes.bool
};

MetricMenu.defaultProps = {
  canExportCsv: false,
  canViewMore: false
};

export default MetricMenu;
